import { useEffect, useId, useMemo, useState } from 'react'
import { v4 as uuidV4 } from 'uuid'
import type { Moment } from 'moment'
import type { SetOptional } from 'type-fest'

import { DATE_SEARCH_OPTION_IDS, type FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL } from 'constants/offer'
import { fetchCruiseOffer } from 'actions/CruiseActions'
import { streamOfferList, updateOfferListKey } from 'actions/OfferActions'
import { useTypeaheadStreaming } from 'hooks/Search/useTypeaheadStreaming'
import useOffers from 'hooks/Offers/useOffers'
import { shallowEqual, useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useStableObjectReference from 'hooks/useStableObjectReference'
import getOfferListKey from 'lib/offer/offerListKey'
import { isBedbankOffer, isLEHotel } from 'lib/offer/offerTypes'
import { allRoomsOccupantsFitRoomCapacity } from 'lib/checkout/cartReservationUtils'

type CarouselOfferProduct = 'hotel' | 'experience' | 'cruise'

interface UseCarouselOfferSearchArgs {
  searchQuery?: string
  cardType?: CarouselOfferProduct
  rooms: Array<App.Occupants>
  filterActive: boolean
  dateMode: DATE_SEARCH_OPTION_IDS
  checkIn?: Moment
  checkOut?: Moment
  flexibleMonths: string
  flexibleNights?: FLEXIBLE_DURATION_RANGE
  originalOfferIds: Array<string>
}

const emptyOfferList: SetOptional<App.OfferList, 'key'> = {
  offerIds: [],
  fetching: true,
  error: null,
}

const emptyDisabledOfferList: SetOptional<App.OfferList, 'key'> = {
  offerIds: [],
  fetching: false,
  error: null,
}

function useCruiseCarouselOffers(offerIds: Array<string>, enabled: boolean) {
  const dispatch = useAppDispatch()
  const stableOfferIds = useStableObjectReference(offerIds)

  useEffect(() => {
    if (!enabled) return
    stableOfferIds.forEach((offerId) => dispatch(fetchCruiseOffer(offerId)))
  }, [dispatch, enabled, stableOfferIds])

  return useAppSelector((state) => {
    const offers = stableOfferIds
      .map((offerId) => state.cruise.cruiseOffers[offerId])
      .filter((offer): offer is App.CruiseOffer => !!offer)

    const isResolved = (offerId: string) =>
      !!state.cruise.cruiseOffers[offerId] || !!state.cruise.cruiseOffersErrors[offerId]

    return {
      offers,
      fetching: enabled && stableOfferIds.some((offerId) =>
        state.cruise.cruiseOffersLoading[offerId] || !isResolved(offerId),
      ),
      resolvedCount: stableOfferIds.filter(isResolved).length,
    }
  }, shallowEqual)
}

function useCarouselHotelOfferList(filters: App.OfferListFilters | undefined, typeaheadId: string): App.OfferList {
  const dispatch = useAppDispatch()
  const listenerId = useId()
  const searchDisabled = !filters
  const listKey = useMemo(() => filters ? getOfferListKey(filters) : '', [filters])

  useEffect(() => {
    if (searchDisabled || !filters) return

    dispatch(streamOfferList(filters, listenerId, undefined, undefined, {
      clientCheckAvailability: true,
      typeaheadId,
    }))
  }, [dispatch, filters, listenerId, searchDisabled, typeaheadId])

  useEffect(() => {
    return () => {
      if (!searchDisabled) {
        dispatch(updateOfferListKey({ offerListKey: listKey, listenerId }))
      }
    }
  }, [dispatch, listKey, listenerId, searchDisabled])

  const offerList = useAppSelector((state) => {
    if (searchDisabled) return emptyDisabledOfferList
    return state.offer.offerLists[listKey] ?? emptyOfferList
  })

  const metaData = useAppSelector(state => state.offer.searchResultMetadata.offerMetaData[listKey])

  return useMemo(() => ({
    ...offerList,
    key: offerList.key ?? listKey,
    offerIds: metaData ? offerList.offerIds.filter(id => {
      const offerData = metaData[id]
      return !offerData || offerData.available
    }) : offerList.offerIds,
  }), [listKey, metaData, offerList])
}

export default function useCarouselOfferSearch({
  searchQuery,
  cardType = 'hotel',
  rooms,
  filterActive,
  dateMode,
  checkIn,
  checkOut,
  flexibleMonths,
  flexibleNights,
  originalOfferIds,
}: UseCarouselOfferSearchArgs) {
  const [typeaheadId] = useState(() => uuidV4())
  const canSearch = !!searchQuery

  const { streamingData } = useTypeaheadStreaming(searchQuery || '', typeaheadId, {
    enabled: canSearch,
    debounceMs: 0,
    temporaryDoNotExtract: false,
  })

  const extractedLocation = streamingData?.location?.data
  const extractedPlaceIds = extractedLocation && 'placeIds' in extractedLocation
    ? extractedLocation.placeIds
    : undefined

  const typeaheadResolved = canSearch && !!streamingData && !streamingData.fetching
  const shouldFilterHotelResults = cardType === 'hotel' && filterActive

  const nlsFilters = useMemo<App.OfferListFilters | undefined>(() => {
    if (!shouldFilterHotelResults || !extractedPlaceIds?.length) return undefined

    const filters: App.OfferListFilters = {
      destinationId: extractedPlaceIds[0],
      destinationIds: extractedPlaceIds,
      rooms,
      offerTypes: [OFFER_TYPE_HOTEL, OFFER_TYPE_BED_BANK],
      limit: 10,
      isStream: true,
    }

    if (dateMode === DATE_SEARCH_OPTION_IDS.SPECIFIC && checkIn && checkOut) {
      filters.checkIn = checkIn.format(ISO_DATE_FORMAT)
      filters.checkOut = checkOut.format(ISO_DATE_FORMAT)
    } else if (dateMode === DATE_SEARCH_OPTION_IDS.FLEXIBLE) {
      if (flexibleMonths) filters.flexibleMonths = flexibleMonths
      if (flexibleNights) filters.flexibleNights = flexibleNights
    }

    return filters
  }, [checkIn, checkOut, dateMode, extractedPlaceIds, flexibleMonths, flexibleNights, rooms, shouldFilterHotelResults])

  const nlsOfferList = useCarouselHotelOfferList(nlsFilters, typeaheadId)

  const activeOfferIds = shouldFilterHotelResults
    ? nlsOfferList.offerIds
    : originalOfferIds

  const shouldUseCruiseOffers = cardType === 'cruise'
  const cruiseOffers = useCruiseCarouselOffers(activeOfferIds, shouldUseCruiseOffers)
  const [genericLoadedOffers, genericOffersFetching] = useOffers<App.AnyOffer>(
    shouldUseCruiseOffers ? [] : activeOfferIds,
  )
  const allLoadedOffers = shouldUseCruiseOffers ? cruiseOffers.offers : genericLoadedOffers
  const offersFetching = shouldUseCruiseOffers ? cruiseOffers.fetching : genericOffersFetching

  // NLS doesn't enforce room capacity constraints, so we verify each offer
  // can accommodate the requested occupancy client-side.
  const offers = useMemo(() => {
    if (!shouldFilterHotelResults) return allLoadedOffers
    return allLoadedOffers.filter(offer => {
      if (isLEHotel(offer)) {
        if (!offer.packages?.length) return true
        return rooms.every((room: App.Occupants) =>
          offer.packages.some(pkg =>
            'roomRate' in pkg && pkg.roomRate?.capacities?.length
              ? allRoomsOccupantsFitRoomCapacity(room, pkg.roomRate.capacities, offer)
              : true,
          ),
        )
      }
      if (isBedbankOffer(offer)) {
        if (!offer.packages?.length) return true
        return rooms.every((room: App.Occupants) =>
          offer.packages.some(pkg =>
            pkg.capacities?.combinations?.length
              ? allRoomsOccupantsFitRoomCapacity(room, pkg.capacities.combinations, offer as App.BedbankOffer)
              : true,
          ),
        )
      }
      return true
    })
  }, [allLoadedOffers, rooms, shouldFilterHotelResults])

  const filteredOfferIds = useMemo(
    () => offers.map(offer => offer.id),
    [offers],
  )

  // When the typeahead finishes but can't resolve a location, treat as loaded
  // (empty) rather than showing loading skeletons indefinitely.
  const isFullyLoaded = shouldFilterHotelResults
    ? (typeaheadResolved && !extractedPlaceIds?.length)
    || (!!nlsFilters && !nlsOfferList.fetching && !offersFetching && activeOfferIds.length === allLoadedOffers.length)
    : shouldUseCruiseOffers
      ? (!offersFetching && activeOfferIds.length > 0 && cruiseOffers.resolvedCount === activeOfferIds.length)
      : (!offersFetching && activeOfferIds.length > 0 && activeOfferIds.length === allLoadedOffers.length)

  const showLoadingCards = !isFullyLoaded
  const showEmptyState = isFullyLoaded && filteredOfferIds.length === 0 && shouldFilterHotelResults

  return {
    offers,
    filteredOfferIds,
    offersFetching,
    isFullyLoaded,
    showLoadingCards,
    showEmptyState,
    typeaheadId,
  }
}
